import debounce from 'lodash/debounce';
import { CloudOff, Search, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { OrgLogoId, OrgLogos } from '@crossnokaye/ux-animatics';
import {
  cn,
  Nav,
  NavItem,
  NavSection,
  NotificationBadge,
  Separator,
  usePersistentState,
} from '@crossnokaye/ux-common';

import { useFilteredOrgFacilities } from '@/components/navigation/hooks/use-filtered-org-facilities';
import { useOfflineFacilities } from '@/components/navigation/hooks/use-offline-facilities';
import { LocalStorageKeys } from '@/constants';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useNavStore } from '@/stores/nav';
import {
  Input,
  InputClear,
  InputField,
  InputLeadingIcon,
} from '@/ux-common/components/input/input';

import { SquareButton } from './shared/square-button';

export const FacilitySelectionNav = ({ switchToMainNav }: { switchToMainNav: () => void }) => {
  const [inputValue, setInputValue] = useState('');

  const handleSwitchToMainNav = () => {
    switchToMainNav();
  };

  const { offlineFacilities, getOfflineFacilityCountByOrg } = useOfflineFacilities();

  const { setFacilitySearchQuery, facilitySearchQuery } = useNavStore();
  const navigate = useNavigate();

  const [value, setValue] = usePersistentState<string[]>(
    localStorage,
    LocalStorageKeys.ExpandedOrganizationSections
  );

  const [defaultExpandedSections, setDefaultExpandedSections] = useState<string[]>(value ?? []);

  const { facilityId: currentFacilityId } = useCurrentFacility();

  const { filteredOrgFacilityList } = useFilteredOrgFacilities();

  useEffect(() => {
    if (facilitySearchQuery) {
      setInputValue(facilitySearchQuery);
    }
  }, [facilitySearchQuery]);

  const handleExpandSection = async (sections: string[]) => {
    setDefaultExpandedSections(sections);
    await setValue(sections);
  };

  const handleSelect = (orgName: string, facilitySlug: string) => {
    navigate({
      to: '.',
      params: (prev) => ({ ...prev, org: orgName, agent: facilitySlug }),
    });
    switchToMainNav();
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFacilitySearchQuery(value);
      }, 300),
    [setFacilitySearchQuery]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    debouncedSearch(newValue);
  };

  const handleClearSearch = () => {
    setInputValue('');
    setFacilitySearchQuery('');
  };

  return (
    <div className={'h-full flex flex-col'} data-testid={'facility-selection-nav'}>
      <div className={'flex-shrink-0 mt-5'}>
        <div className={'flex justify-between items-center mb-4 mx-3'}>
          <div className={'text-lg font-semibold text-foreground'}>{'ATLAS Facilities'}</div>
          <SquareButton
            icon={<X className="glyph glyph-sm" aria-hidden="true" />}
            onClick={handleSwitchToMainNav}
          />
        </div>
        <div className={'mb-5 mx-2.5'}>
          <Separator className={'bg-border mb-4'} />
          {/* `bg-popover` goes on `InputField`, not the group: `.input-field` paints
              its own `--input-bg` over the group root. */}
          <Input className="input-reserve-clear">
            <InputField
              className={'bg-popover'}
              aria-label={'Search Facilities'}
              placeholder={'Search Facilities'}
              onChange={handleSearch}
              value={inputValue}
            >
              <InputLeadingIcon>
                <Search />
              </InputLeadingIcon>
              {inputValue && (
                <InputClear
                  aria-label={'Clear search'}
                  data-testid={'clear-search-button'}
                  onClick={handleClearSearch}
                />
              )}
            </InputField>
          </Input>
        </div>
      </div>
      <div className={'flex-grow overflow-y-auto scrollbar-hide'}>
        <Nav
          className={'justify-start w-full'}
          defaultOpen={defaultExpandedSections}
          onOpenChange={handleExpandSection}
        >
          {filteredOrgFacilityList?.map((org) => {
            const offlineFacilityCount = getOfflineFacilityCountByOrg(org.organizationId);
            const OrgLogoIcon = OrgLogos[org.iconName as OrgLogoId];

            return (
              <NavSection
                key={org.organizationId}
                header={org.displayName}
                icon={OrgLogoIcon ? <OrgLogoIcon size={'1.5rem'} color={'white'} /> : null}
                className={'font-bold text-xs mb-4 px-3.5 '}
                triggerClassName={'flex flex-auto items-center w-full gap-2'}
                data-testid={`facility-selection-nav-org-${org.organizationId}`}
                rightIcon={
                  offlineFacilityCount > 0 && (
                    <NotificationBadge
                      className={'bg-warning w-5 h-5 text-black ml-auto'}
                      count={offlineFacilityCount}
                      data-testid={'offline-badge'}
                    />
                  )
                }
              >
                {(org.facilities || []).map((fac) => {
                  const isOffline = offlineFacilities.has(fac.facilityId);
                  return (
                    <div className={'flex flex-col'} key={fac.facilityId}>
                      <NavItem
                        key={fac.facilityId}
                        id={fac.facilityId}
                        label={fac.displayName}
                        className={cn(
                          'truncate mt-2 px-3 hover:bg-popover',
                          isOffline ? 'text-warning' : ''
                        )}
                        selected={fac.facilityId === currentFacilityId}
                        onClick={() => handleSelect(org.name, fac.shortName)}
                        data-testid={`facility-selection-nav-fac-${fac.facilityId}`}
                        rightIcon={
                          isOffline && (
                            <CloudOff
                              className="glyph glyph-sm text-warning"
                              data-testid="offline-cloud-icon"
                              aria-hidden="true"
                            />
                          )
                        }
                      />
                    </div>
                  );
                })}
              </NavSection>
            );
          })}
          {filteredOrgFacilityList?.length === 0 && (
            <div className={'text-muted-foreground'}>{'No matching facilities found.'}</div>
          )}
        </Nav>
      </div>
    </div>
  );
};
